import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE = "norbu_cart";

function readInitial() {
  if (typeof window === "undefined") return [];
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE) || "[]");
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  // Read from localStorage synchronously on mount so we never overwrite it with [].
  const [items, setItems] = useState(readInitial);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    localStorage.setItem(STORAGE, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (x) => x.product_id === item.product_id && x.size === item.size && x.color === item.color
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
        return next;
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev.map((x) =>
        `${x.product_id}-${x.size}-${x.color}` === key ? { ...x, quantity: Math.max(1, qty) } : x
      )
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((x) => `${x.product_id}-${x.size}-${x.color}` !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((acc, i) => acc + i.price * i.quantity, 0);
  const totalQty = items.reduce((acc, i) => acc + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items, addItem, updateQty, removeItem, clear,
        subtotal, totalQty, drawerOpen, setDrawerOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
